import React, {useState} from 'react';
import {Helmet} from '@dr.pogodin/react-helmet';
import {DomainCheckerForm} from './DMARC/Checker/DomainCheckerForm';
import {BookCallModal} from './common/BooKCallModal';
import heroImage from '../../assets/img/undraw_business-deal_nx2n.svg'
import scan from '../../assets/img/scan.svg'
import report from '../../assets/img/report.svg'
import share from '../../assets/img/share.svg'
import earn from '../../assets/img/earn.svg'
import noDns from '../../assets/img/no-dns.svg'
import deliverability from '../../assets/img/deliverability.svg'
import commission from '../../assets/img/commission.svg'
import euroPlan from '../../assets/img/euro-plan.svg'
import baseHostname from "../../hostname";
import {routes} from "../../routes";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {I18nService} from "../../service/i18nService";
import {useTranslation} from "react-i18next";

export const DMARCflowPartnerPage = () => {
    const [domain, setDomain] = useState('');
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const i18nService = new I18nService();
    const currentLang = i18nService.getLanguage();
    const {t} = useTranslation();

    const fetchDmarcRecord = async (e) => {
        e.preventDefault();
        setLoading(true);
        fetch(`${baseHostname}/index.php/domain/analyze`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({source: 'DMARC', domain})
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data) {
                    dispatch({type: 'SET_DMARC_ANALYZE_RESULT', payload: {result: data, domain: domain}});
                    navigate(`/${currentLang}/${routes['dmarcResult'][currentLang]}`);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    return (
        <>
            <Helmet>
                <html lang={currentLang}/>
                <title>{t('pages.partnerPage.meta.title')}</title>
                <meta name="description" content={t('pages.partnerPage.meta.description')}/>
                <meta name="robots" content="index, follow"/>
                <meta property="og:title" content={t('pages.partnerPage.meta.ogTitle')}/>
                <meta property="og:description" content={t('pages.partnerPage.meta.ogDescription')}/>
                <meta property="og:image" content={`${baseHostname}/share-image.jpg`}/>
                <meta property="og:url" content={`${baseHostname}/${currentLang}/partner`}/>
                <meta property="og:type" content="website"/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:title" content={t('pages.partnerPage.meta.title')}/>
                <meta name="twitter:description" content={t('pages.partnerPage.meta.description')}/>
                <meta name="twitter:image" content={`${baseHostname}/share-image.jpg`}/>
                <link rel="canonical" href={`${baseHostname}/${currentLang}/partner`}/>
            </Helmet>

            {/* HERO */}
            <header className="bg-primary text-white p-5" role="banner">
                <div className="container p-5">
                    <div className="row align-items-center text-center text-lg-start pt-5 pb-5">
                        <div className="col-lg-8 mx-auto mx-lg-0">
                            <h1 className="display-4 fw-bold" itemProp="headline">
                                {t('pages.partnerPage.hero.headline')}
                            </h1>
                            <p className="lead mt-3" itemProp="description">
                                {t('pages.partnerPage.hero.description')}
                            </p>
                            <button
                                className="btn btn-light btn-lg mt-4 shadow"
                                onClick={() => setShowModal(true)}
                                aria-label={t('pages.partnerPage.hero.cta')}
                            >
                                {t('pages.partnerPage.hero.cta')}
                            </button>
                        </div>
                        <div className="col-lg-4 d-none d-lg-block text-center">
                            <img
                                src={heroImage}
                                alt="Partner illustration"
                                className="img-fluid"
                                width="320"
                                height="220"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* DOMAIN SCANNER */}
            <section className="bg-light py-5" id="domain-scan" aria-labelledby="domain-scan-heading">
                <div className="container justify-content-center d-flex text-center">
                    <div className="row justify-content-center align-items-center text-center text-md-start">
                        <div className="col">
                            <h2 id="domain-scan-heading" className="fw-bold text-center">
                                {t('pages.partnerPage.scanner.title')}
                            </h2>
                            <p className="text-muted mb-4 text-center">{t('pages.partnerPage.scanner.description')}</p>
                            <div className="d-flex justify-content-center text-center align-items-center">
                                <DomainCheckerForm
                                    domain={domain}
                                    setDomain={setDomain}
                                    fetchDmarcRecord={fetchDmarcRecord}
                                    loading={loading}
                                />
                            </div>
                            <p className="small text-muted mt-3 text-center">{t('pages.partnerPage.scanner.hint1')}</p>
                            <p className="small text-muted mt-3 text-center">{t('pages.partnerPage.scanner.hint2')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-5 bg-white" id="how-it-works" aria-labelledby="how-it-works-heading">
                <div className="container">
                    <h2 id="how-it-works-heading"
                        className="text-center mb-5 fw-bold">{t('pages.partnerPage.howItWorks.title')}</h2>
                    <p className="text-muted text-center mb-4">{t('pages.partnerPage.howItWorks.description')}</p>
                    <div className="row g-4 text-center">
                        {t('pages.partnerPage.howItWorks.steps', {returnObjects: true}).map(({title, desc}, i) => (
                            <div className="col-sm-6 col-lg-3" key={i}>
                                <div
                                    className="bg-light p-4 rounded shadow-sm h-100 d-flex flex-column align-items-center">
                                    <img src={[scan, report, share, earn][i]} alt={title} width="64" height="64"
                                         className="mb-3" loading="lazy"/>
                                    <h3 className="fw-semibold h5">{title}</h3>
                                    <p className="text-muted small mb-0">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="bg-light py-5" id="partner-benefits" aria-labelledby="partner-benefits-heading">
                <div className="container">
                    <h2 id="partner-benefits-heading"
                        className="fw-bold text-center mb-4">{t('pages.partnerPage.benefits.title')}</h2>
                    <p className="text-muted text-center mb-5">{t('pages.partnerPage.benefits.description')}</p>

                    <div className="row g-4 text-center">
                        {t('pages.partnerPage.benefits.points', {returnObjects: true}).map((text, i) => (
                            <div className="col-sm-6 col-lg-6" key={i}>
                                <div
                                    className="bg-white p-4 rounded shadow-sm h-100 d-flex flex-column align-items-center">
                                    <img src={[noDns, deliverability, commission, euroPlan][i]} alt="" width="48"
                                         height="48" className="mb-3" loading="lazy"/>
                                    <p className="mb-0">{text}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row justify-content-center mt-5">
                        <div className="col-lg-10 col-xl-8">
                            <div className="bg-white border rounded shadow-sm p-4">
                                <h3 className="fw-bold text-center mb-3">{t('pages.partnerPage.benefits.commission.title')}</h3>
                                <p className="text-center lead">{t('pages.partnerPage.benefits.commission.description')}</p>

                                <div className="card border-0 shadow-sm my-4">
                                    <div className="card-body">
                                        <h4 className="card-title fw-semibold h5">{t('pages.partnerPage.benefits.commission.tiersTitle')}</h4>
                                        <ul className="list-unstyled mb-0">
                                            {t('pages.partnerPage.benefits.commission.tiers', {returnObjects: true}).map((tier, i) => (
                                                <li key={i}>{tier}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <p className="mb-4 text-center">{t('pages.partnerPage.benefits.commission.footer')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ SECTION */}
            <section className="py-5 bg-white" id="faq" aria-labelledby="faq-heading">
                <div className="container">
                    <h2 id="faq-heading" className="text-center mb-0 fw-bold">
                        {t('pages.partnerPage.faq.title')}
                    </h2>
                    <p className="text-muted mb-5 text-center">{t('pages.partnerPage.faq.description')}</p>

                    <div className="accordion border-0" id="faqAccordion">
                        {t('pages.partnerPage.faq.questions', {returnObjects: true}).map(({q, a}, i) => (
                            <div className="accordion-item border-0 border-bottom" key={i}>
                                <h3 className="accordion-header" id={`heading-${i}`}>
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target={`#collapse-${i}`}
                                        aria-expanded="false"
                                        aria-controls={`collapse-${i}`}
                                    >
                                        {q}
                                    </button>
                                </h3>
                                <div
                                    id={`collapse-${i}`}
                                    className="accordion-collapse collapse"
                                    aria-labelledby={`heading-${i}`}
                                    data-bs-parent="#faqAccordion"
                                >
                                    <div className="accordion-body">{a}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-primary text-white text-center py-5" id="cta" aria-labelledby="cta-heading">
                <div className="container">
                    <h2 id="cta-heading">{t('pages.partnerPage.cta.title')}</h2>
                    <p className="lead mt-2">{t('pages.partnerPage.cta.description')}</p>
                    <button className="btn btn-light btn-lg mt-3" onClick={() => setShowModal(true)}>
                        {t('pages.partnerPage.cta.button')}
                    </button>
                </div>
            </section>

            <BookCallModal show={showModal} handleClose={() => setShowModal(false)}/>
        </>
    );
};